import { useMemo } from 'react'

import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'

export const useWindowUrl = (windowId: string) => {
  const containerURL = process.env.REACT_APP_CONTAINER_DOMAIN

  const window = useWindowStore(state =>
    state.windows.find(window => window.id === windowId)
  )

  const { apps } = useAvailableApps()

  const rootPath = useMemo(() => {
    if (!window) return ''

    if (window.id in apps === false) {
      return ''
    }

    return apps[window.id as keyof typeof apps]?.rootPath || ''
  }, [window, apps])

  const basePath = useMemo(() => {
    if (!window) return ''

    if (window.id in apps === false) {
      return ''
    }

    return apps[window.id as keyof typeof apps]?.path.replace(rootPath, '')
  }, [rootPath, window, apps])

  const currentWindowURL = useMemo(() => {
    if (!window) return ''

    if (!basePath) {
      return ''
    }

    return (
      window.history
        ?.at(window.currentHistoryIndex)
        ?.replace(basePath.startsWith('/') ? basePath : `/${basePath}`, '') ||
      ''
    )
  }, [basePath, window])

  const fullWindowURL = useMemo(() => {
    return `${containerURL}${rootPath}${basePath}${currentWindowURL}`
  }, [basePath, containerURL, currentWindowURL, rootPath])

  return fullWindowURL
}

export const useGetWindowUrl = () => {
  const containerURL = process.env.REACT_APP_CONTAINER_DOMAIN

  const windows = useWindowStore(state => state.windows)
  const { apps } = useAvailableApps()

  return (windowId: string) => {
    const window = windows.find(window => window.id === windowId)

    const rootPath = (() => {
      if (!window) return ''

      if (window.id in apps === false) {
        return ''
      }

      return apps[window.id as keyof typeof apps]?.rootPath || ''
    })()

    const basePath = (() => {
      if (!window) return ''

      if (window.id in apps === false) {
        return ''
      }

      return apps[window.id as keyof typeof apps]?.path.replace(rootPath, '')
    })()

    const currentWindowURL = (() => {
      if (!window) return ''

      if (!basePath) {
        return ''
      }

      return (
        window.history
          ?.at(window.currentHistoryIndex)
          ?.replace(basePath.startsWith('/') ? basePath : `/${basePath}`, '') ||
        ''
      )
    })()

    const fullWindowURL = (() => {
      return `${containerURL}${rootPath}${basePath}${currentWindowURL}`
    })()

    return fullWindowURL
  }
}
